/**
 * LinkChecklistItemDialog Component
 *
 * Allows linking a document to a checklist item from the document detail view.
 * Shows checklist items across all audits (or filtered by a selected audit),
 * with audit and category context per item.
 */

import { useState, useMemo, useCallback } from "react";
import { ListChecks, Search, Loader2, ChevronDown } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Select from "@radix-ui/react-select";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui";
import { api } from "convex/_generated/api";
import { useLinkDocument } from "@/hooks/use-link-document";
import { useWorkspaceQuery } from "@/hooks";
import { useLinkedChecklistItems } from "@/hooks/use-linked-checklist-items";
import type { Id } from "@/packages/types";

interface ChecklistItemRowProps {
  item: {
    _id: string;
    description: string;
    categoryName: string;
    required: boolean;
  };
  selected: boolean;
  showCategory: boolean;
  onSelect: (id: string) => void;
}

function ChecklistItemRow({
  item,
  selected,
  showCategory,
  onSelect,
}: ChecklistItemRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item._id)}
      className={`w-full text-left flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
        selected
          ? "bg-accent/10 border border-accent/30"
          : "hover:bg-card-hover border border-transparent"
      }`}
    >
      <div
        className={`rounded-md p-1.5 shrink-0 mt-0.5 ${
          selected ? "bg-accent/20" : "bg-accent/10"
        }`}
      >
        <ListChecks
          size={16}
          className={selected ? "text-accent" : "text-accent/70"}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-primary leading-snug">
          {item.description}
        </p>
        {(showCategory && item.categoryName) || item.required ? (
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {showCategory && item.categoryName && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-muted/20 text-muted leading-none">
                {item.categoryName}
              </span>
            )}
            {item.required && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400 leading-none">
                Required
              </span>
            )}
          </div>
        ) : null}
      </div>
      {selected && (
        <div className="w-4 h-4 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8.33335 2.91669L3.75002 7.50002L1.66669 5.41669"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </button>
  );
}

interface LinkChecklistItemDialogProps {
  documentId: Id<"documents">;
  auditId?: Id<"audits">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LinkChecklistItemDialog({
  documentId,
  auditId,
  open,
  onOpenChange,
}: LinkChecklistItemDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [filterAuditId, setFilterAuditId] = useState<string>("all");

  // Reset filter to document's audit when dialog opens
  const effectiveFilter = useMemo(
    () => (filterAuditId === "all" ? null : (filterAuditId as Id<"audits">)),
    [filterAuditId],
  );

  const { data: audits, isLoading: auditsLoading } = useWorkspaceQuery(
    api.checklist_queries.getAuditsForFilter,
    {},
    { enabled: open },
  );

  const { data: allItems, isLoading: itemsLoading } = useWorkspaceQuery(
    api.checklist_queries.getItemsWithAuditInfo,
    { filterAuditId: effectiveFilter },
    { enabled: open },
  );

  const { items: linkedItems } = useLinkedChecklistItems(documentId);

  const linkedIds = useMemo(
    () => new Set(linkedItems.map((i) => i.checklistItemId)),
    [linkedItems],
  );

  const filteredItems = useMemo(() => {
    const available = (allItems ?? []).filter(
      (item) => !linkedIds.has(item._id),
    );
    if (!searchQuery.trim()) return available;
    const q = searchQuery.toLowerCase();
    return available.filter(
      (item) =>
        item.description.toLowerCase().includes(q) ||
        item.auditName.toLowerCase().includes(q) ||
        item.categoryName.toLowerCase().includes(q),
    );
  }, [allItems, linkedIds, searchQuery]);

  const groupedByAudit = useMemo(() => {
    const groups = new Map<
      string,
      { auditName: string; items: typeof filteredItems }
    >();
    for (const item of filteredItems) {
      const key = item.auditId;
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(key, { auditName: item.auditName, items: [item] });
      }
    }
    return [...groups.values()];
  }, [filteredItems]);

  const linkDocument = useLinkDocument();

  const handleLink = useCallback(async () => {
    if (!selectedItemId) return;
    setIsLinking(true);
    try {
      await linkDocument({
        documentId,
        checklistItemId: selectedItemId as Id<"checklistItems">,
      });
      onOpenChange(false);
      setSelectedItemId(null);
      setSearchQuery("");
    } finally {
      setIsLinking(false);
    }
  }, [selectedItemId, documentId, linkDocument, onOpenChange]);

  const handleClose = useCallback(() => {
    setSelectedItemId(null);
    setSearchQuery("");
    onOpenChange(false);
  }, [onOpenChange]);

  // Reset filter to document's audit when dialog opens/closes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setFilterAuditId(auditId ?? "all");
        setSelectedItemId(null);
        setSearchQuery("");
      }
      onOpenChange(isOpen);
    },
    [auditId, onOpenChange],
  );

  const isLoading = itemsLoading || auditsLoading;
  const hasNoItems = !isLoading && filteredItems.length === 0 && !searchQuery;
  const isEmpty = !isLoading && filteredItems.length === 0;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content
              asChild
              onPointerDownOutside={(e) => e.preventDefault()}
            >
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[520px] rounded-[12px] bg-card shadow-xl"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <Dialog.Title className="text-[15px] font-semibold text-primary">
                      Link to checklist item
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-card-hover transition-colors"
                        aria-label="Close dialog"
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-muted"
                          aria-hidden="true"
                        >
                          <path
                            d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    {/* Filters row */}
                    <div className="flex gap-2 mb-3">
                      {/* Audit filter */}
                      <Select.Root
                        value={filterAuditId}
                        onValueChange={(val) => {
                          setFilterAuditId(val);
                          setSelectedItemId(null);
                        }}
                      >
                        <Select.Trigger className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-[13px] text-primary bg-card hover:bg-card-hover focus:outline-none focus:ring-2 focus:ring-accent/40 transition-colors min-w-[160px] max-w-[220px]">
                          <Select.Value placeholder="All Audits" />
                          <ChevronDown
                            size={14}
                            className="text-muted ml-auto shrink-0"
                          />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content
                            className="z-[70] bg-card border border-border rounded-lg shadow-lg overflow-hidden py-1"
                            position="popper"
                            sideOffset={4}
                          >
                            <Select.Viewport>
                              <Select.Item
                                value="all"
                                className="flex items-center px-3 py-2 text-[13px] text-primary hover:bg-card-hover cursor-pointer focus:outline-none focus:bg-card-hover"
                              >
                                <Select.ItemText>All Audits</Select.ItemText>
                              </Select.Item>
                              {(audits ?? []).map((audit) => (
                                <Select.Item
                                  key={audit.id}
                                  value={audit.id}
                                  className="flex items-center px-3 py-2 text-[13px] text-primary hover:bg-card-hover cursor-pointer focus:outline-none focus:bg-card-hover"
                                >
                                  <Select.ItemText>
                                    {audit.displayName}
                                  </Select.ItemText>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>

                      {/* Search */}
                      <div className="relative flex-1">
                        <Search
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                        />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search checklist items…"
                          className="w-full h-9 pl-9 pr-3 rounded-lg border border-border text-[13px] text-primary bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                        />
                      </div>
                    </div>

                    {/* List */}
                    <div className="max-h-[320px] overflow-y-auto">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2
                            size={20}
                            className="text-muted animate-spin"
                          />
                        </div>
                      ) : hasNoItems ? (
                        <div className="text-center py-8">
                          <ListChecks
                            size={32}
                            className="mx-auto mb-3 text-muted/40"
                          />
                          <p className="text-[13px] font-medium text-secondary mb-1">
                            All items already linked
                          </p>
                          <p className="text-[12px] text-muted">
                            This document is linked to all available checklist
                            items.
                          </p>
                        </div>
                      ) : isEmpty ? (
                        <div className="text-center py-8">
                          <p className="text-[13px] text-muted">
                            No items match your search.
                          </p>
                        </div>
                      ) : filterAuditId === "all" ? (
                        <div className="space-y-4">
                          {groupedByAudit.map((group) => (
                            <div key={group.auditName}>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted px-1 mb-1">
                                {group.auditName}
                              </p>
                              <div className="space-y-1">
                                {group.items.map((item) => (
                                  <ChecklistItemRow
                                    key={item._id}
                                    item={item}
                                    selected={selectedItemId === item._id}
                                    showCategory
                                    onSelect={setSelectedItemId}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredItems.map((item) => (
                            <ChecklistItemRow
                              key={item._id}
                              item={item}
                              selected={selectedItemId === item._id}
                              showCategory
                              onSelect={setSelectedItemId}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
                    <Button variant="secondary" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleLink}
                      disabled={!selectedItemId || isLinking || hasNoItems}
                    >
                      {isLinking ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Linking…
                        </>
                      ) : (
                        "Link item"
                      )}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
